/**
 * Product Governance Console — internal Suntronix super-admin view.
 *
 * Tabbed console covering the 17-point governance framework:
 *   1. Releases    2. Feature Flags   3. Notices     4. Maintenance
 *   5. Incidents   6. Feedback        7. Analytics   8. Quality Gates
 *
 * Every action here is auditable; nothing here bypasses tenant isolation.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ScrollText, Flag, Megaphone, Wrench, AlertOctagon, MessageSquare,
  BarChart3, ShieldCheck, Plus, RefreshCw, Send,
} from "lucide-react";

const TABS = [
  { key: "releases",    label: "Releases",       icon: ScrollText },
  { key: "flags",       label: "Feature flags",  icon: Flag },
  { key: "notices",     label: "Notices",        icon: Megaphone },
  { key: "maintenance", label: "Maintenance",    icon: Wrench },
  { key: "incidents",   label: "Incidents",      icon: AlertOctagon },
  { key: "feedback",    label: "Feedback",       icon: MessageSquare },
  { key: "adoption",    label: "Analytics",      icon: BarChart3 },
  { key: "gates",       label: "Quality gates",  icon: ShieldCheck },
];

export default function GovernanceConsole() {
  const [tab, setTab] = useState("releases");

  return (
    <div className="max-w-6xl space-y-6" data-testid="governance-console">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Product Governance</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">Change & Release Console</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          One place for every customer-visible change: releases, feature flags, notices, maintenance windows, incidents,
          customer feedback, adoption metrics and pre-release quality gates. Nothing ships to &ldquo;all&rdquo; without passing through this console.
        </p>
      </div>

      <div className="border-b border-slate-200 flex flex-wrap gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`gov-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={
              "px-3 h-9 text-sm rounded-t-md flex items-center gap-2 border-b-2 -mb-px transition-colors " +
              (tab === t.key
                ? "border-slate-900 text-slate-900 font-medium bg-slate-50"
                : "border-transparent text-slate-500 hover:text-slate-900")
            }
          >
            <t.icon className="h-4 w-4" strokeWidth={1.6} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "releases"    && <ReleasesTab />}
      {tab === "flags"       && <FlagsTab />}
      {tab === "notices"     && <NoticesTab />}
      {tab === "maintenance" && <MaintenanceTab />}
      {tab === "incidents"   && <IncidentsTab />}
      {tab === "feedback"    && <FeedbackTab />}
      {tab === "adoption"    && <AdoptionTab />}
      {tab === "gates"       && <GatesTab />}
    </div>
  );
}

/* ---------- Releases ---------- */
function ReleasesTab() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = () => api.get("/governance/releases", { params: { published_only: false } })
    .then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);

  return (
    <section className="space-y-4">
      <SectionHead
        title="Releases"
        hint="Track every production release: major, minor, patch and security."
        onNew={() => setShowForm(true)}
        onRefresh={load}
        newLabel="Draft release"
        testIdBase="release"
      />

      {showForm && <ReleaseForm onClose={() => setShowForm(false)} onCreated={load} />}

      <div className="bg-white border border-slate-200 rounded-md divide-y">
        {items.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No releases yet.</div>
        ) : items.map((r) => (
          <div key={r.id} className="p-4 flex items-start gap-4" data-testid={`release-row-${r.version}`}>
            <div className="w-24 shrink-0">
              <div className="text-xs font-mono text-slate-500">{r.release_date}</div>
              <div className={`inline-block mt-1 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border ${releaseTypeStyle(r.release_type)}`}>{r.release_type}</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="text-sm font-semibold text-slate-900">v{r.version}</div>
                {!r.is_published && <span className="text-[10px] font-semibold uppercase tracking-widest text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5">Draft</span>}
                {r.change_classification === "breaking" && <span className="text-[10px] font-semibold uppercase tracking-widest text-rose-800 bg-rose-50 border border-rose-200 rounded px-1.5">Breaking</span>}
              </div>
              <div className="text-sm text-slate-800 mt-0.5">{r.headline || "(untitled)"}</div>
              <div className="text-xs text-slate-500 mt-1">
                {(r.features?.length || 0)} new · {(r.improvements?.length || 0)} improved · {(r.fixes?.length || 0)} fixed
                {(r.known_issues?.length || 0) > 0 && ` · ${r.known_issues.length} known issue(s)`}
              </div>
            </div>
            <div className="shrink-0">
              {!r.is_published && (
                <button
                  data-testid={`release-publish-${r.version}`}
                  className="h-8 px-3 text-xs rounded-md border border-slate-200 bg-white hover:bg-slate-50"
                  onClick={async () => { await api.post(`/governance/releases/${r.id}/publish`); toast.success(`v${r.version} published`); load(); }}
                >
                  Publish
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ReleaseForm({ onClose, onCreated }) {
  const [form, setForm] = useState({
    version: "", release_type: "minor", headline: "", change_classification: "customer_experience",
    features: "", improvements: "", fixes: "", known_issues: "", security_notes: "",
  });
  const submit = async () => {
    const toList = (s) => s.split("\n").map((l) => l.trim()).filter(Boolean).map((title) => ({ title, detail: "" }));
    try {
      await api.post("/governance/releases", {
        version: form.version,
        release_type: form.release_type,
        headline: form.headline,
        change_classification: form.change_classification,
        features: toList(form.features),
        improvements: toList(form.improvements),
        fixes: toList(form.fixes),
        known_issues: toList(form.known_issues),
        security_notes: form.security_notes || null,
      });
      toast.success("Release drafted");
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid="release-form">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Field label="Version"><input data-testid="release-version" className="input" placeholder="1.1.0" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} /></Field>
        <Field label="Type">
          <select data-testid="release-type" className="input" value={form.release_type} onChange={(e) => setForm({ ...form, release_type: e.target.value })}>
            {["major", "minor", "patch", "security"].map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Change classification" wide>
          <select data-testid="release-class" className="input" value={form.change_classification} onChange={(e) => setForm({ ...form, change_classification: e.target.value })}>
            {["no_impact", "low_impact", "customer_experience", "workflow", "integration", "security", "breaking"].map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Headline (customer-friendly)"><input data-testid="release-headline" className="input" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} /></Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="New features (one per line)"><textarea data-testid="release-features" className="input min-h-[80px]" value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} /></Field>
        <Field label="Improvements"><textarea className="input min-h-[80px]" value={form.improvements} onChange={(e) => setForm({ ...form, improvements: e.target.value })} /></Field>
        <Field label="Fixes"><textarea className="input min-h-[80px]" value={form.fixes} onChange={(e) => setForm({ ...form, fixes: e.target.value })} /></Field>
        <Field label="Known issues"><textarea className="input min-h-[80px]" value={form.known_issues} onChange={(e) => setForm({ ...form, known_issues: e.target.value })} /></Field>
      </div>
      <Field label="Security notes (optional)"><textarea className="input min-h-[60px]" value={form.security_notes} onChange={(e) => setForm({ ...form, security_notes: e.target.value })} /></Field>
      <div className="flex gap-2 justify-end">
        <button className="h-9 px-3 rounded-md text-sm text-slate-600 hover:bg-slate-100" onClick={onClose}>Cancel</button>
        <button data-testid="release-save" className="h-9 px-3 rounded-md text-sm bg-slate-900 text-white hover:bg-slate-800" onClick={submit}>Save draft</button>
      </div>
    </div>
  );
}

function releaseTypeStyle(t) {
  return {
    major:    "text-fuchsia-700 border-fuchsia-200 bg-fuchsia-50",
    minor:    "text-sky-700 border-sky-200 bg-sky-50",
    patch:    "text-slate-600 border-slate-200 bg-slate-50",
    security: "text-rose-700 border-rose-200 bg-rose-50",
  }[t] || "text-slate-600 border-slate-200 bg-slate-50";
}

/* ---------- Feature Flags ---------- */
function FlagsTab() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = () => api.get("/governance/feature-flags").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);

  const updateStage = async (id, stage) => {
    await api.patch(`/governance/feature-flags/${id}`, { stage });
    toast.success(`Rolled to ${stage}`);
    load();
  };

  return (
    <section className="space-y-4">
      <SectionHead title="Feature flags" hint="Every risky feature ships behind a flag. Promote a flag through Internal → Demo → Pilot → Selected → All."
        onNew={() => setShowForm(true)} onRefresh={load} newLabel="New flag" testIdBase="flag" />

      {showForm && <FlagForm onClose={() => setShowForm(false)} onCreated={load} />}

      <div className="bg-white border border-slate-200 rounded-md divide-y">
        {items.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No flags yet.</div>
        ) : items.map((f) => (
          <div key={f.id} className="p-4 flex items-start gap-4" data-testid={`flag-row-${f.key}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="text-sm font-semibold text-slate-900">{f.name}</div>
                {f.preview && <span className="text-[10px] font-semibold uppercase tracking-widest text-fuchsia-700 bg-fuchsia-50 border border-fuchsia-200 rounded px-1.5">Preview</span>}
              </div>
              <div className="text-xs font-mono text-slate-500 mt-0.5">{f.key}</div>
              {f.description && <div className="text-xs text-slate-600 mt-1">{f.description}</div>}
              <div className="text-[11px] text-slate-500 mt-1.5">Enabled tenants: {(f.enabled_tenants?.length || 0)}</div>
            </div>
            <div className="shrink-0 flex flex-wrap gap-1">
              {["internal", "demo", "pilot", "selected", "all"].map((s) => (
                <button
                  key={s}
                  data-testid={`flag-stage-${f.key}-${s}`}
                  onClick={() => updateStage(f.id, s)}
                  className={
                    "text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded border " +
                    (f.stage === s
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-300")
                  }
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FlagForm({ onClose, onCreated }) {
  const [f, setF] = useState({ key: "", name: "", description: "", stage: "internal", preview: false });
  const save = async () => {
    try {
      await api.post("/governance/feature-flags", f);
      toast.success("Flag created");
      onCreated();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid="flag-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Key"><input data-testid="flag-key" className="input" placeholder="new-asset-360" value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })} /></Field>
        <Field label="Display name"><input data-testid="flag-name" className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
      </div>
      <Field label="Description"><textarea className="input min-h-[60px]" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
      <div className="flex items-center gap-6">
        <Field label="Initial stage">
          <select className="input" value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value })}>
            {["internal", "demo", "pilot", "selected", "all"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <label className="flex items-center gap-2 text-sm text-slate-700 pt-6">
          <input type="checkbox" data-testid="flag-preview" checked={f.preview} onChange={(e) => setF({ ...f, preview: e.target.checked })} />
          Preview feature (customer admin can opt-in)
        </label>
      </div>
      <div className="flex gap-2 justify-end">
        <button className="h-9 px-3 rounded-md text-sm text-slate-600 hover:bg-slate-100" onClick={onClose}>Cancel</button>
        <button data-testid="flag-save" className="h-9 px-3 rounded-md text-sm bg-slate-900 text-white hover:bg-slate-800" onClick={save}>Create</button>
      </div>
    </div>
  );
}

/* ---------- Notices ---------- */
function NoticesTab() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = () => api.get("/governance/notices").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);

  return (
    <section className="space-y-4">
      <SectionHead title="Advance & integration notices" hint="Tell customers about API changes, auth changes, workflow changes and integration health BEFORE they happen."
        onNew={() => setShowForm(true)} onRefresh={load} newLabel="New notice" testIdBase="notice" />

      {showForm && <NoticeForm onClose={() => setShowForm(false)} onCreated={load} />}

      <div className="bg-white border border-slate-200 rounded-md divide-y">
        {items.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No active notices.</div>
        ) : items.map((n) => (
          <div key={n.id} className="p-4 space-y-1" data-testid={`notice-row-${n.id}`}>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border ${severityStyle(n.severity)}`}>{n.severity}</span>
              <span className="text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border border-slate-200 text-slate-600 bg-slate-50">{n.category}</span>
              {n.action_required && <span className="text-[10px] font-semibold uppercase tracking-widest text-rose-800 bg-rose-50 border border-rose-200 rounded px-1.5">Action required</span>}
              <span className="text-sm font-semibold text-slate-900 ml-1">{n.title}</span>
            </div>
            <div className="text-sm text-slate-700">{n.message}</div>
            <div className="text-xs text-slate-500">
              {n.effective_date && <span>Effective {n.effective_date}. </span>}
              {n.deadline && <span>Deadline {n.deadline}. </span>}
              {(n.affected_services?.length > 0) && <span>Affects: {n.affected_services.join(", ")}. </span>}
              Tenants: {(n.affected_tenants?.length > 0) ? n.affected_tenants.length : "All"}.
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function NoticeForm({ onClose, onCreated }) {
  const [n, setN] = useState({
    title: "", message: "", category: "advance", severity: "info",
    action_required: false, effective_date: "", deadline: "",
    affected_services: "", integration_key: "",
  });
  const save = async () => {
    try {
      await api.post("/governance/notices", {
        ...n,
        affected_services: n.affected_services.split(",").map((s) => s.trim()).filter(Boolean),
        effective_date: n.effective_date || null,
        deadline: n.deadline || null,
        integration_key: n.integration_key || null,
      });
      toast.success("Notice published");
      onCreated(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid="notice-form">
      <Field label="Title"><input data-testid="notice-title" className="input" value={n.title} onChange={(e) => setN({ ...n, title: e.target.value })} /></Field>
      <Field label="Message"><textarea data-testid="notice-message" className="input min-h-[80px]" value={n.message} onChange={(e) => setN({ ...n, message: e.target.value })} /></Field>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Field label="Category">
          <select className="input" value={n.category} onChange={(e) => setN({ ...n, category: e.target.value })}>
            {["advance", "integration", "policy"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Severity">
          <select className="input" value={n.severity} onChange={(e) => setN({ ...n, severity: e.target.value })}>
            {["info", "warning", "critical"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Effective date"><input type="date" className="input" value={n.effective_date} onChange={(e) => setN({ ...n, effective_date: e.target.value })} /></Field>
        <Field label="Deadline"><input type="date" className="input" value={n.deadline} onChange={(e) => setN({ ...n, deadline: e.target.value })} /></Field>
      </div>
      <Field label="Affected services (comma separated)"><input className="input" value={n.affected_services} onChange={(e) => setN({ ...n, affected_services: e.target.value })} /></Field>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={n.action_required} onChange={(e) => setN({ ...n, action_required: e.target.checked })} />
        Action required by customer
      </label>
      <div className="flex gap-2 justify-end">
        <button className="h-9 px-3 rounded-md text-sm text-slate-600 hover:bg-slate-100" onClick={onClose}>Cancel</button>
        <button data-testid="notice-save" className="h-9 px-3 rounded-md text-sm bg-slate-900 text-white hover:bg-slate-800" onClick={save}>Publish notice</button>
      </div>
    </div>
  );
}

/* ---------- Maintenance ---------- */
function MaintenanceTab() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = () => api.get("/governance/maintenance").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);

  return (
    <section className="space-y-4">
      <SectionHead title="Planned maintenance" hint="Communicate downtime windows in advance."
        onNew={() => setShowForm(true)} onRefresh={load} newLabel="Schedule window" testIdBase="maintenance" />
      {showForm && <MaintenanceForm onClose={() => setShowForm(false)} onCreated={load} />}
      <div className="bg-white border border-slate-200 rounded-md divide-y">
        {items.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No windows scheduled.</div>
        ) : items.map((m) => (
          <div key={m.id} className="p-4 flex items-start gap-4">
            <div className={`text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border ${maintenanceStatusStyle(m.status)}`}>{m.status}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900">{m.title}</div>
              <div className="text-xs text-slate-500">{m.starts_at} → {m.ends_at} ({m.timezone})</div>
              <div className="text-xs text-slate-600 mt-1">Impact: <span className="font-medium">{m.expected_impact}</span>{m.affected_services?.length ? ` · ${m.affected_services.join(", ")}` : ""}</div>
              {m.notes && <div className="text-xs text-slate-500 mt-1">{m.notes}</div>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function maintenanceStatusStyle(s) {
  return {
    scheduled:  "text-sky-700 bg-sky-50 border-sky-200",
    in_progress:"text-amber-700 bg-amber-50 border-amber-200",
    completed:  "text-emerald-700 bg-emerald-50 border-emerald-200",
  }[s] || "text-slate-600 bg-slate-50 border-slate-200";
}

function MaintenanceForm({ onClose, onCreated }) {
  const [m, setM] = useState({ title: "", starts_at: "", ends_at: "", timezone: "UTC", expected_impact: "minimal", affected_services: "", notes: "" });
  const save = async () => {
    try {
      await api.post("/governance/maintenance", {
        ...m,
        affected_services: m.affected_services.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Scheduled");
      onCreated(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid="maintenance-form">
      <Field label="Title"><input className="input" value={m.title} onChange={(e) => setM({ ...m, title: e.target.value })} /></Field>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Start (ISO)"><input className="input" placeholder="2026-03-01T02:00:00Z" value={m.starts_at} onChange={(e) => setM({ ...m, starts_at: e.target.value })} /></Field>
        <Field label="End (ISO)"><input className="input" placeholder="2026-03-01T04:00:00Z" value={m.ends_at} onChange={(e) => setM({ ...m, ends_at: e.target.value })} /></Field>
        <Field label="Timezone"><input className="input" value={m.timezone} onChange={(e) => setM({ ...m, timezone: e.target.value })} /></Field>
        <Field label="Expected impact">
          <select className="input" value={m.expected_impact} onChange={(e) => setM({ ...m, expected_impact: e.target.value })}>
            {["minimal", "degraded", "outage"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Affected services (comma separated)"><input className="input" value={m.affected_services} onChange={(e) => setM({ ...m, affected_services: e.target.value })} /></Field>
      <Field label="Notes"><textarea className="input min-h-[60px]" value={m.notes} onChange={(e) => setM({ ...m, notes: e.target.value })} /></Field>
      <div className="flex gap-2 justify-end">
        <button className="h-9 px-3 rounded-md text-sm text-slate-600 hover:bg-slate-100" onClick={onClose}>Cancel</button>
        <button className="h-9 px-3 rounded-md text-sm bg-slate-900 text-white hover:bg-slate-800" onClick={save}>Schedule</button>
      </div>
    </div>
  );
}

/* ---------- Incidents ---------- */
function IncidentsTab() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = () => api.get("/governance/incidents").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);

  const addUpdate = async (id, state, note) => {
    if (!note) return;
    await api.post(`/governance/incidents/${id}/updates`, { state, note });
    toast.success(`Update posted (${state})`);
    load();
  };

  return (
    <section className="space-y-4">
      <SectionHead title="Incidents" hint="Never hide service degradation. Communicate status honestly."
        onNew={() => setShowForm(true)} onRefresh={load} newLabel="Open incident" testIdBase="incident" />
      {showForm && <IncidentForm onClose={() => setShowForm(false)} onCreated={load} />}
      <div className="space-y-3">
        {items.length === 0 ? (
          <div className="p-6 text-sm text-slate-500 bg-white border border-slate-200 rounded-md">No incidents.</div>
        ) : items.map((i) => (
          <div key={i.id} className="bg-white border border-slate-200 rounded-md p-4" data-testid={`incident-row-${i.id}`}>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border ${severityStyle(i.severity === "critical" ? "critical" : i.severity === "major" ? "warning" : "info")}`}>{i.severity}</span>
              <span className={`text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border ${incidentStateStyle(i.state)}`}>{i.state}</span>
              <span className="text-sm font-semibold text-slate-900 ml-1">{i.title}</span>
              <span className="text-xs text-slate-500 ml-auto">Started {new Date(i.started_at).toLocaleString("en-GB")}</span>
            </div>
            <ol className="mt-3 border-l border-slate-200 pl-4 space-y-2 text-xs">
              {(i.updates || []).slice().reverse().map((u, idx) => (
                <li key={idx}>
                  <span className={`text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border mr-1.5 ${incidentStateStyle(u.state)}`}>{u.state}</span>
                  <span className="text-slate-700">{u.note}</span>
                  <span className="text-slate-400 ml-2">{new Date(u.at).toLocaleString("en-GB")}</span>
                </li>
              ))}
            </ol>
            {i.state !== "resolved" && <IncidentUpdater onSubmit={(s, note) => addUpdate(i.id, s, note)} />}
          </div>
        ))}
      </div>
    </section>
  );
}

function IncidentUpdater({ onSubmit }) {
  const [state, setState] = useState("identified");
  const [note, setNote] = useState("");
  return (
    <div className="mt-3 flex gap-2 items-end">
      <select className="input h-8 max-w-[140px]" value={state} onChange={(e) => setState(e.target.value)} data-testid="incident-update-state">
        {["investigating", "identified", "monitoring", "resolved"].map((s) => <option key={s}>{s}</option>)}
      </select>
      <input data-testid="incident-update-note" className="input h-8 flex-1" placeholder="Post an update…" value={note} onChange={(e) => setNote(e.target.value)} />
      <button data-testid="incident-update-post" onClick={() => { onSubmit(state, note); setNote(""); }} className="h-8 px-3 rounded-md text-xs bg-slate-900 text-white hover:bg-slate-800 flex items-center gap-1"><Send className="h-3 w-3" /> Post</button>
    </div>
  );
}

function IncidentForm({ onClose, onCreated }) {
  const [i, setI] = useState({ title: "", severity: "minor", affected_services: "", initial_state: "investigating", initial_note: "We're investigating a potential issue." });
  const save = async () => {
    try {
      await api.post("/governance/incidents", {
        ...i,
        affected_services: i.affected_services.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Incident opened");
      onCreated(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid="incident-form">
      <Field label="Title"><input data-testid="incident-title" className="input" value={i.title} onChange={(e) => setI({ ...i, title: e.target.value })} /></Field>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Field label="Severity">
          <select className="input" value={i.severity} onChange={(e) => setI({ ...i, severity: e.target.value })}>
            {["minor", "major", "critical"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Initial state">
          <select className="input" value={i.initial_state} onChange={(e) => setI({ ...i, initial_state: e.target.value })}>
            {["investigating", "identified", "monitoring"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Affected services"><input className="input" placeholder="API, Notifications" value={i.affected_services} onChange={(e) => setI({ ...i, affected_services: e.target.value })} /></Field>
      </div>
      <Field label="Initial note"><textarea className="input min-h-[60px]" value={i.initial_note} onChange={(e) => setI({ ...i, initial_note: e.target.value })} /></Field>
      <div className="flex gap-2 justify-end">
        <button className="h-9 px-3 rounded-md text-sm text-slate-600 hover:bg-slate-100" onClick={onClose}>Cancel</button>
        <button data-testid="incident-save" className="h-9 px-3 rounded-md text-sm bg-slate-900 text-white hover:bg-slate-800" onClick={save}>Open incident</button>
      </div>
    </div>
  );
}

/* ---------- Feedback ---------- */
function FeedbackTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/governance/feedback").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);
  const move = async (id, status) => {
    await api.patch(`/governance/feedback/${id}`, { status });
    toast.success(`Marked ${status.replace("_", " ")}`);
    load();
  };
  const STATES = ["new", "under_review", "planned", "not_planned", "delivered"];

  return (
    <section className="space-y-4">
      <SectionHead title="Customer feedback" hint="Every request classified. Customers see the same status their submission carries." onRefresh={load} />
      <div className="bg-white border border-slate-200 rounded-md divide-y">
        {items.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No feedback yet.</div>
        ) : items.map((f) => (
          <div key={f.id} className="p-4" data-testid={`feedback-row-${f.id}`}>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border ${feedbackStyle(f.status)}`}>{f.status?.replace("_", " ")}</span>
              <span className="text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border border-slate-200 text-slate-500 bg-slate-50">{f.category}</span>
              <span className="text-sm font-semibold text-slate-900 ml-1">{f.title}</span>
              <span className="text-xs text-slate-500 ml-auto">{f.submitter_email}</span>
            </div>
            <div className="text-xs text-slate-600 mt-1">{f.description}</div>
            <div className="mt-2 flex flex-wrap gap-1">
              {STATES.map((s) => (
                <button key={s} onClick={() => move(f.id, s)}
                  data-testid={`feedback-move-${f.id}-${s}`}
                  className={
                    "text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " +
                    (f.status === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300")
                  }
                >{s.replace("_", " ")}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function feedbackStyle(s) {
  return {
    new:          "text-slate-700 bg-slate-50 border-slate-200",
    under_review: "text-sky-700 bg-sky-50 border-sky-200",
    planned:      "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200",
    not_planned:  "text-slate-500 bg-slate-50 border-slate-200",
    delivered:    "text-emerald-700 bg-emerald-50 border-emerald-200",
  }[s] || "text-slate-500 bg-slate-50 border-slate-200";
}

/* ---------- Adoption ---------- */
function AdoptionTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/governance/adoption").then(({ data }) => setItems(data.items || [])); }, []);
  return (
    <section className="space-y-4">
      <SectionHead title="Feature adoption" hint="Aggregate metrics only. No employee-level tracking." />
      <div className="bg-white border border-slate-200 rounded-md divide-y">
        {items.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No adoption events recorded yet. Instrument features via <code className="font-mono text-slate-700">POST /api/governance/telemetry</code>.</div>
        ) : items.map((i) => (
          <div key={i.feature_key + i.tenant_id} className="p-4 grid grid-cols-2 md:grid-cols-6 gap-2 text-sm items-center" data-testid={`adopt-row-${i.feature_key}`}>
            <div className="col-span-2">
              <div className="font-semibold text-slate-900">{i.feature_key}</div>
              <div className="text-xs text-slate-500 font-mono">tenant {i.tenant_id?.slice(0, 8) || "—"}</div>
            </div>
            <Metric label="Views"       value={i.views || 0} />
            <Metric label="Starts"      value={i.starts || 0} />
            <Metric label="Completion"  value={i.completion_rate != null ? `${i.completion_rate}%` : "—"} />
            <Metric label="Rating"      value={i.avg_rating != null ? `${i.avg_rating} / 5` : "—"} />
          </div>
        ))}
      </div>
    </section>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">{label}</div>
      <div className="text-lg font-heading font-semibold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}

/* ---------- Quality gates ---------- */
function GatesTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/governance/quality-gates").then(({ data }) => setData(data)); }, []);
  if (!data) return null;
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-heading font-semibold text-slate-900">Release quality gates</h2>
        <p className="text-sm text-slate-600 mt-1">{data.policy}</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-md divide-y">
        {data.gates.map((g) => (
          <div key={g.name} className="p-4 flex items-center gap-4">
            <span className={`text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border ${gateStyle(g.status)}`}>{g.status}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900">{g.name} {g.required && <span className="text-[10px] text-rose-700 uppercase tracking-widest ml-1">Required</span>}</div>
              <div className="text-xs text-slate-500 mt-0.5">{g.detail}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function gateStyle(s) {
  return {
    pass:    "text-emerald-700 bg-emerald-50 border-emerald-200",
    partial: "text-amber-700 bg-amber-50 border-amber-200",
    pending: "text-slate-600 bg-slate-50 border-slate-200",
    gated:   "text-sky-700 bg-sky-50 border-sky-200",
    fail:    "text-rose-700 bg-rose-50 border-rose-200",
  }[s] || "text-slate-500 bg-slate-50 border-slate-200";
}

/* ---------- Shared helpers ---------- */
function SectionHead({ title, hint, onNew, onRefresh, newLabel = "New", testIdBase }) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex-1 min-w-0">
        <h2 className="text-lg font-heading font-semibold text-slate-900">{title}</h2>
        {hint && <div className="text-xs text-slate-500 mt-0.5">{hint}</div>}
      </div>
      {onRefresh && (
        <button onClick={onRefresh} data-testid={testIdBase ? `${testIdBase}-refresh` : undefined}
          className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 flex items-center gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      )}
      {onNew && (
        <button onClick={onNew} data-testid={testIdBase ? `${testIdBase}-new` : undefined}
          className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white hover:bg-slate-800 flex items-center gap-1.5">
          <Plus className="h-3.5 w-3.5" /> {newLabel}
        </button>
      )}
    </div>
  );
}

function Field({ label, children, wide }) {
  return (
    <label className={`block ${wide ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

function severityStyle(s) {
  return {
    info:     "text-sky-700 bg-sky-50 border-sky-200",
    warning:  "text-amber-700 bg-amber-50 border-amber-200",
    critical: "text-rose-700 bg-rose-50 border-rose-200",
  }[s] || "text-slate-500 bg-slate-50 border-slate-200";
}

function incidentStateStyle(s) {
  return {
    investigating: "text-amber-700 bg-amber-50 border-amber-200",
    identified:    "text-sky-700 bg-sky-50 border-sky-200",
    monitoring:    "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200",
    resolved:      "text-emerald-700 bg-emerald-50 border-emerald-200",
  }[s] || "text-slate-500 bg-slate-50 border-slate-200";
}
